<template>
    <v-container fluid class="pa-4">
        <!-- Header -->
        <v-row class="mb-2">
            <v-col cols="12" class="d-flex align-center justify-space-between">
                <div>
                    <h1 class="text-h5 font-weight-bold">Driver Safety Analytics</h1>
                    <p class="text-body-2 text-grey mt-1">Fleet-wide driver safety performance and trends</p>
                </div>
                <v-btn icon="mdi-refresh" variant="text" :loading="loading" @click="fetchData"/>
            </v-col>
        </v-row>

        <!-- Filters -->
        <v-row class="mb-4">
            <v-col cols="12" sm="4" md="3">
                <v-select v-model="dateRange" :items="dateRangeOptions" item-title="label" item-value="value"
                          label="Time Period" density="compact" variant="outlined" hide-details
                          @update:model-value="fetchData"/>
            </v-col>
            <v-col cols="12" sm="4" md="3" v-if="isAdmin">
                <company-select v-model="selectedCompany" :items="companies"
                                label="Company" density="compact" variant="outlined" hide-details clearable
                                @update:model-value="fetchData"/>
            </v-col>
        </v-row>

        <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-4"/>

        <template v-if="data && !loading">
            <!-- KPI Cards Row -->
            <v-row class="mb-4">
                <v-col cols="6" sm="3" md="2">
                    <v-card elevation="2" class="text-center pa-3">
                        <div class="text-h4 font-weight-bold" :class="scoreTextColor(data.fleet_avg_score)">
                            {{ data.fleet_avg_score ?? '-' }}
                        </div>
                        <div class="text-caption text-grey">Fleet Avg Score</div>
                    </v-card>
                </v-col>
                <v-col cols="6" sm="3" md="2">
                    <v-card elevation="2" class="text-center pa-3">
                        <div class="text-h4 font-weight-bold">{{ data.total_drivers ?? 0 }}</div>
                        <div class="text-caption text-grey">Drivers Scored</div>
                    </v-card>
                </v-col>
                <v-col cols="6" sm="3" md="2">
                    <v-card elevation="2" class="text-center pa-3">
                        <div class="text-h4 font-weight-bold">{{ data.event_totals?.total_trips ?? 0 }}</div>
                        <div class="text-caption text-grey">Total Trips</div>
                    </v-card>
                </v-col>
                <v-col cols="6" sm="3" md="2">
                    <v-card elevation="2" class="text-center pa-3">
                        <div class="text-h4 font-weight-bold">
                            {{ strOperations().unitifyDistance(data.event_totals?.total_miles, 'distance', 'miles') }}
                        </div>
                        <div class="text-caption text-grey">Total Distance</div>
                    </v-card>
                </v-col>
                <v-col cols="6" sm="3" md="2">
                    <v-card elevation="2" class="text-center pa-3">
                        <div class="text-h4 font-weight-bold text-red">{{ data.event_totals?.speeding ?? 0 }}</div>
                        <div class="text-caption text-grey">Speeding Events</div>
                    </v-card>
                </v-col>
                <v-col cols="6" sm="3" md="2">
                    <v-card elevation="2" class="text-center pa-3">
                        <div class="text-h4 font-weight-bold text-warning">
                            {{ data.event_totals?.harsh_braking ?? 0 }}
                        </div>
                        <div class="text-caption text-grey">Harsh Braking</div>
                    </v-card>
                </v-col>
            </v-row>

            <!-- Charts Row -->
            <v-row class="mb-4">
                <!-- Score Distribution Doughnut -->
                <v-col cols="12" md="4">
                    <v-card elevation="2" class="pa-4">
                        <h3 class="text-subtitle-1 font-weight-bold mb-3">Score Distribution</h3>
                        <Doughnut v-if="distributionChartData" :data="distributionChartData"
                                  :options="doughnutOptions"/>
                        <div v-else class="text-center text-grey py-8">No distribution data</div>
                    </v-card>
                </v-col>
                <!-- Category Breakdown Bar -->
                <v-col cols="12" md="4">
                    <v-card elevation="2" class="pa-4">
                        <h3 class="text-subtitle-1 font-weight-bold mb-3">Category Averages</h3>
                        <Bar v-if="categoryChartData" :data="categoryChartData" :options="barOptions"/>
                        <div v-else class="text-center text-grey py-8">No category data</div>
                    </v-card>
                </v-col>
                <!-- Fleet Trend Line -->
                <v-col cols="12" md="4">
                    <v-card elevation="2" class="pa-4">
                        <h3 class="text-subtitle-1 font-weight-bold mb-3">Fleet Score Trend</h3>
                        <Line v-if="trendChartData" :data="trendChartData" :options="lineOptions"/>
                        <div v-else class="text-center text-grey py-8">No trend data</div>
                    </v-card>
                </v-col>
            </v-row>

            <!-- Event Breakdown -->
            <v-row class="mb-4">
                <v-col cols="12">
                    <v-card elevation="2" class="pa-4">
                        <h3 class="text-subtitle-1 font-weight-bold mb-3">Event Summary</h3>
                        <v-row>
                            <v-col cols="6" sm="4" md="2" v-for="evt in eventCards" :key="evt.label">
                                <div class="text-center">
                                    <v-icon :color="evt.color" size="28">{{ evt.icon }}</v-icon>
                                    <div class="text-h6 font-weight-bold mt-1">{{ evt.value }}</div>
                                    <div class="text-caption text-grey">{{ evt.label }}</div>
                                </div>
                            </v-col>
                        </v-row>
                    </v-card>
                </v-col>
            </v-row>

            <!-- Coaching Summary -->
            <v-row v-if="data.coaching_summary" class="mb-4">
                <v-col cols="12">
                    <v-card elevation="2" class="pa-4">
                        <h3 class="text-subtitle-1 font-weight-bold mb-3">Coaching</h3>
                        <v-row>
                            <v-col cols="6" sm="4" md="2" v-for="c in coachingCards" :key="c.label">
                                <div class="text-center">
                                    <v-icon :color="c.color" size="28">{{ c.icon }}</v-icon>
                                    <div class="text-h6 font-weight-bold mt-1">{{ c.value }}</div>
                                    <div class="text-caption text-grey">{{ c.label }}</div>
                                </div>
                            </v-col>
                        </v-row>
                    </v-card>
                </v-col>
            </v-row>

            <!-- Driver Rankings Table -->
            <v-row>
                <v-col cols="12">
                    <v-card elevation="2" class="pa-4">
                        <h3 class="text-subtitle-1 font-weight-bold mb-3">Driver Rankings</h3>
                        <v-table density="compact" hover>
                            <thead>
                            <tr>
                                <th>#</th>
                                <th>Driver</th>
                                <th class="text-center">Overall</th>
                                <th class="text-center">Speeding</th>
                                <th class="text-center">Braking</th>
                                <th class="text-center">Accel</th>
                                <th class="text-center">Idling</th>
                                <th class="text-center">Cornering</th>
                                <th class="text-center">Discipline</th>
                                <th class="text-center">Distance</th>
                                <th class="text-center">Trips</th>
                                <th class="text-center">Last Coached</th>
                                <th class="text-center">Coach</th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-for="(driver, i) in data.driver_rankings" :key="driver.driver_id">
                                <td>{{ i + 1 }}</td>
                                <td>
                                    <div class="d-flex align-center">
                                        <UserAvatar :src="driver.photo" :name="driver.name" size="28" class="mr-2"/>
                                        {{ driver.name }}
                                        <v-chip v-if="driver.is_provisional" size="x-small" variant="outlined"
                                                class="ml-1">P
                                        </v-chip>
                                    </div>
                                </td>
                                <td class="text-center">
                                    <v-chip v-if="driver.overall_score != null" size="small" variant="flat"
                                            :color="driverScoreColor(driver.overall_score)">
                                        {{ driver.overall_score }}
                                    </v-chip>
                                    <span v-else>-</span>
                                </td>
                                <td class="text-center">{{ driver.speeding_score }}</td>
                                <td class="text-center">{{ driver.braking_score }}</td>
                                <td class="text-center">{{ driver.acceleration_score }}</td>
                                <td class="text-center">{{ driver.idling_score }}</td>
                                <td class="text-center">{{ driver.cornering_score }}</td>
                                <td class="text-center">{{ driver.discipline_score }}</td>
                                <td class="text-center">
                                    {{ strOperations().unitifyDistance(driver.total_miles, 'distance', 'miles') }}
                                </td>
                                <td class="text-center">{{ driver.total_trips }}</td>
                                <td class="text-center">
                                    <v-chip v-if="driver.last_coached_at" size="x-small" variant="tonal"
                                            :color="driver.open_coaching_count ? 'warning' : 'success'">
                                        {{ fmtCoachDate(driver.last_coached_at) }}
                                        <span v-if="driver.open_coaching_count"> &middot; {{ driver.open_coaching_count }} open</span>
                                    </v-chip>
                                    <v-chip v-else size="x-small" variant="tonal" color="grey">Never</v-chip>
                                </td>
                                <td class="text-center">
                                    <v-chip v-if="driver.retired" size="x-small" variant="tonal" color="grey">Retired</v-chip>
                                    <v-btn v-else size="x-small" variant="tonal" color="primary"
                                           prepend-icon="mdi-school"
                                           @click="goCoach(driver.driver_id)">Coach</v-btn>
                                </td>
                            </tr>
                            </tbody>
                        </v-table>
                        <div v-if="!data.driver_rankings?.length" class="text-center text-grey py-4">
                            No driver score data available for this period.
                        </div>
                    </v-card>
                </v-col>
            </v-row>
        </template>
    </v-container>
</template>

<script setup>
import {ref, computed, onMounted} from 'vue'
import {Doughnut, Bar, Line} from 'vue-chartjs'
import {Chart, registerables} from 'chart.js'
import {send} from '@/composables/apiClient'
import UserAvatar from '@/components/UserAvatar.vue'
import {useAuthStore} from '@/stores/authStore'
import {strOperations} from '@/composables/globals'
import {useRouter} from 'vue-router'
import {useTheme} from 'vuetify'
import CompanySelect from "@/components/globals/company-select.vue";

Chart.register(...registerables)

const router = useRouter()
const authStore = useAuthStore()
const {user} = authStore

// Open a driver's Coaching tab straight from the rankings - turns the analytics
// table into an actionable "drivers to coach" worklist.
const goCoach = (driverId) => {
    if (!driverId) return
    router.push({name: 'driver', params: {driver: driverId}, query: {tab: 'coaching'}})
}
const isAdmin = computed(() => user?.type === 'admin')
const loading = ref(false)
const data = ref(null)

const fmtCoachDate = (d) => {
    if (!d) return ''
    const dt = new Date(d)
    return isNaN(dt) ? '' : dt.toLocaleDateString(undefined, {month: 'short', day: 'numeric', year: 'numeric'})
}

const coachingCards = computed(() => {
    const c = data.value?.coaching_summary
    if (!c) return []
    return [
        {label: 'Needs Coaching', value: c.needs_coaching ?? 0, icon: 'mdi-account-alert', color: 'error'},
        {label: 'Never Coached', value: c.never_coached ?? 0, icon: 'mdi-account-question', color: 'warning'},
        {label: 'Coached 30d', value: c.recently_coached ?? 0, icon: 'mdi-school', color: 'success'},
        {label: 'Open Follow-ups', value: c.open_followups ?? 0, icon: 'mdi-clipboard-clock-outline', color: 'info'},
        {label: 'Coached', value: c.coached ?? 0, icon: 'mdi-account-check', color: 'primary'},
        {label: '% Events Coached', value: c.coached_pct != null ? `${c.coached_pct}%` : '-', icon: 'mdi-check-decagram', color: 'success'},
        {label: 'Avg Days to Coach', value: c.avg_days_to_coach != null ? c.avg_days_to_coach : '-', icon: 'mdi-timer-outline', color: 'info'},
        {label: 'Repeat After Coaching', value: c.repeat_after_coaching ?? 0, icon: 'mdi-repeat', color: 'warning'},
    ]
})
const companies = ref([])
const selectedCompany = ref(user?.type !== 'admin' ? user?.company?.id : null)

const dateRangeOptions = [
    {label: 'Last 7 Days', value: 7},
    {label: 'Last 30 Days', value: 30},
    {label: 'Last 90 Days', value: 90},
]
const dateRange = ref(30)

// The middle band uses the theme warning everywhere on this page, charts included.
const theme = useTheme()
const warningColor = computed(() => theme.current.value.colors.warning)

const driverScoreColor = (score) => {
    if (score >= 8.0) return 'success'
    if (score >= 5.0) return 'warning'
    return 'error'
}

const scoreTextColor = (score) => {
    if (score == null) return ''
    if (score >= 8.0) return 'text-green'
    if (score >= 5.0) return 'text-warning'
    return 'text-red'
}

const doughnutOptions = {
    responsive: true,
    cutout: '55%',
    plugins: {
        legend: {position: 'bottom', labels: {boxWidth: 14, padding: 12}}
    }
}

const barOptions = {
    responsive: true,
    indexAxis: 'y',
    scales: {x: {min: 0, max: 10}},
    plugins: {legend: {display: false}}
}

const lineOptions = {
    responsive: true,
    scales: {y: {min: 0, max: 10}},
    plugins: {legend: {display: false}},
    elements: {point: {radius: 2}, line: {tension: 0.3}}
}

const distributionChartData = computed(() => {
    if (!data.value?.distribution) return null
    const d = data.value.distribution
    return {
        labels: ['Excellent (8-10)', 'Good (5-7.9)', 'Needs Improvement (<5)'],
        datasets: [{
            data: [d.excellent, d.good, d.needs_improvement],
            backgroundColor: ['#4CAF50', warningColor.value, '#F44336'],
        }]
    }
})

const categoryChartData = computed(() => {
    if (!data.value?.category_averages) return null
    const c = data.value.category_averages
    const categories = [
        {label: 'Speeding', value: c.speeding},
        {label: 'Braking', value: c.braking},
        {label: 'Acceleration', value: c.acceleration},
        {label: 'Idling', value: c.idling},
        {label: 'Cornering', value: c.cornering},
        {label: 'Discipline', value: c.discipline},
    ]
    return {
        labels: categories.map(cat => cat.label),
        datasets: [{
            data: categories.map(cat => cat.value),
            backgroundColor: categories.map(cat =>
                cat.value >= 8 ? '#4CAF50' : cat.value >= 5 ? warningColor.value : '#F44336'
            ),
        }]
    }
})

const trendChartData = computed(() => {
    if (!data.value?.trend?.length) return null
    return {
        labels: data.value.trend.map(t => t.period_end),
        datasets: [{
            data: data.value.trend.map(t => t.avg_score),
            borderColor: '#1976D2',
            backgroundColor: 'rgba(25, 118, 210, 0.1)',
            fill: true,
        }]
    }
})

const eventCards = computed(() => {
    if (!data.value?.event_totals) return []
    const e = data.value.event_totals
    return [
        {label: 'Speeding', value: e.speeding ?? 0, icon: 'mdi-speedometer', color: 'red'},
        {label: 'Harsh Braking', value: e.harsh_braking ?? 0, icon: 'mdi-car-brake-alert', color: 'warning'},
        {label: 'Hard Accel', value: e.hard_acceleration ?? 0, icon: 'mdi-car-traction-control', color: 'amber'},
        {label: 'Sharp Corners', value: e.sharp_cornering ?? 0, icon: 'mdi-rotate-right', color: 'purple'},
        {label: 'Idle Hours', value: e.total_idle_hours ?? 0, icon: 'mdi-timer-sand', color: 'blue-grey'},
        {label: 'Night Hours', value: e.total_night_hours ?? 0, icon: 'mdi-weather-night', color: 'indigo'},
    ]
})

const fetchData = async () => {
    loading.value = true
    const endDate = new Date()
    const startDate = new Date()
    startDate.setDate(startDate.getDate() - dateRange.value)

    const params = {
        start_date: startDate.toISOString().split('T')[0],
        end_date: endDate.toISOString().split('T')[0],
    }
    if (selectedCompany.value) params.company = selectedCompany.value

    try {
        const response = await send('get', 'drivers/safety-analytics', params)
        data.value = response?.data?.data
    } catch (e) {
        console.error('Failed to fetch safety analytics:', e)
    } finally {
        loading.value = false
    }
}

onMounted(() => {
    fetchData()
    if (isAdmin.value) {
        send('get', 'companies', {list: true}).then(r => {
            companies.value = r?.data?.data ?? []
        }).catch(e => console.warn('Could not load companies filter', e))
    }
})
</script>
